import React, { useCallback, useEffect, useRef, useState } from "react";
import { useBeforeUnload } from "react-router-dom";
import {
  addEdge,
  useNodesState,
  useEdgesState,
  applyNodeChanges,
  applyEdgeChanges,
  Connection,
  EdgeChange,
  NodeChange,
  MarkerType,
  Edge,
  useReactFlow,
  OnConnectEnd,
  OnConnectStart,
  Node
} from "reactflow";
import "reactflow/dist/style.css";
import CustomEdge from "../components/CustomEdge.tsx";
import BuilderSurface from "../components/BuilderSurface.tsx";
import ContentMenu from "../components/ContentMenu.tsx";
import SIDEPANEL from "../constants/SidepanelContent.ts";
import addComponent from "../components/AddComponents.tsx";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import CanvasHeader from "../components/CanvasHeader.tsx";
import { useFlowStore } from "../store/FlowStateStore.ts";
import { CanvasNode, CanvasNodeContent, CanvasNodeData } from "../models/CanvasNode.ts";
import { CanvasEdge } from "../models/CanvasEdge.ts";
import CustomNode from "../components/CustomNode.tsx";
import WhatsappFlow from "../models/WhatsappFlow.ts";
import axios from "axios";
import { Environment as environment } from "../environment.ts";
import { setAuthToken } from "../api/http.ts";
import { isBodyLinkAllowed } from "../utils/BodyLinkRules.ts";

const nodeTypes = { custom: CustomNode };
const edgeTypes = { custom: CustomEdge };

export default function Canvas() {

  const reactFlowWrapper = useRef<HTMLDivElement>(null);
  const { project } = useReactFlow();

  const [btnLoading, setBtnLoading] = useState<boolean>(false);

  const { nodes, edges, setNodes, setEdges, flowId, businessId, setFlowId, setBusinessId, fetchBusinessAttributes, fetchSystemAttributes } = useFlowStore();
  const [rfNodes, setRfNodes] = useNodesState(nodes as unknown as Node[]);
  const [rfEdges, setRfEdges] = useEdgesState(edges as unknown as Edge[]);
  const [staffId, setStaffId] = useState<string>();
  const [flowStatus, setFlowStatus] = useState<boolean>(true);
  const [phoneNumberId, setPhoneNumberId] = useState<string>();
  const [businessDetail, setBusinessDetail] = useState<any>();
  const [staff, setStaff] = useState<any>();

  const addContentInfo = SIDEPANEL["Message Type"];

  const [isConnecting, setIsConnecting] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [dropPosition, setDropPosition] = useState({ x: 0, y: 0 });

  // Lets AddComponents.tsx (used by SidePanel, which sits outside ReactFlowProvider)
  // know where the center of the user's current view is, so newly-added nodes land
  // where they're looking instead of always near Flow Start's original position.
  useEffect(() => {
    useFlowStore.getState().setGetViewportCenter(() => {
      if (!reactFlowWrapper.current) return null;
      const bounds = reactFlowWrapper.current.getBoundingClientRect();
      return project({ x: bounds.width / 2, y: bounds.height / 2 });
    });
  }, [project]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    for (let [key, value] of urlParams.entries()) {
      switch (key) {
        case 'flowId':
          setFlowId(value);
          break;
        case 'businessId':
          setBusinessId(value);
          break;
        case 'staffId':
          setStaffId(value);
          break;
        case 'phoneNumberId':
          setPhoneNumberId(value);
          break;
        case 'token':
          if (value && value !== 'undefined' && value !== 'null') {
            setAuthToken(value);
          }
          break;
      }
    }
  }, [setBusinessId, setFlowId]);

  useEffect(() => {
    if (!!businessId) {
      axios.get(`${environment.baseUrl}/business/${businessId}`).then((response) => {
        setBusinessDetail(response.data);
      }).catch((error) => {
        toast.warn(`Failed to fetch business details. Refresh Page`, { autoClose: 2000 });
      });
      // Fetch attributes for the variable library
      fetchBusinessAttributes();
      fetchSystemAttributes();
    }
  }, [businessId, fetchBusinessAttributes, fetchSystemAttributes]);

  useEffect(() => {
    if (!!businessId && !!staffId) {
      const payload = {
        "businessId": businessId,
        "agentId": staffId,
        "page": 0,
        "size": 1
      }
      axios.put(`${environment.baseUrl}/fetch/agent`, payload).then((response) => {
        setStaff(response?.data?.data?.data?.[0] ?? null);
      }).catch((error) => {
        toast.warn(`Failed to fetch staff details. Refresh Page`, { autoClose: 2000 });
      });
    }
  }, [businessId, staffId]);

  // ---- Delete edge ----
  const deleteEdge = useCallback((edgeId: string) => {
    setRfEdges((eds) => {
      const updatedEdges = eds.filter((e) => e.id !== edgeId);
      setEdges(updatedEdges as unknown as CanvasEdge[]);
      return updatedEdges;
    });
  }, [setEdges, setRfEdges]);

  useBeforeUnload((event) => {
    event.preventDefault();
    event.returnValue = "⚠️ Unsaved changes will be lost.";
  });

  useEffect(() => {
    if (!flowId) return;

    axios
      .get(`${environment.baseUrl}/business/flow/${flowId}`)
      .then((res) => {
        setFlowName(res.data.name);
        setFlowStatus(res.data.status);
        const { nodes = [], edges = [] } = res.data.flowContent;

        const normalizedNodes = nodes.map((node: any) => ({
          ...node,
          id: String(node.id),
          position: {
            x: parseFloat(node.position.x),
            y: parseFloat(node.position.y),
          },
          type: node.type || 'custom',
        }));

        const normalizedEdges = edges.map((edge: any) => ({
          id: String(edge.id),
          source: String(edge.source),
          target: String(edge.target),
          sourceHandle: edge.sourceHandle || null,
          targetHandle: edge.targetHandle || null,
          type: 'custom',
          animated: edge.animated === true || edge.animated === 'true',
          markerEnd: { type: MarkerType.ArrowClosed },
          style: edge.style || {},
          data: { ...(edge.data || {}), onDelete: deleteEdge },
        }));
        setNodes(normalizedNodes);
        setEdges(normalizedEdges);
      })
      .catch((err) => console.error('Error fetching flow:', err));
  }, [flowId, setNodes, setEdges, deleteEdge]);


  // Captures the drag's source node/handle, and whether it actually turned into an edge, so
  // onConnectEnd can explain a rejected drop. React Flow never calls onConnect once
  // isValidConnection has already said no for a real drag-and-drop, so the toast that lives in
  // onConnect (below) is otherwise unreachable from that path — only onConnectEnd sees every drop.
  const connectStartRef = useRef<{ nodeId: string | null; handleId: string | null } | null>(null);
  const connectionMadeRef = useRef(false);

  // ---- Body-connector rule ----
  // Resolved against the live node list so both the drag-time affordance (isValidConnection,
  // which greys out handles that can't accept the drop) and the drop-time guard below share
  // exactly one definition of what's legal.
  const nodeTypeById = useCallback(
    (id?: string | null) =>
      (rfNodes.find((n) => n.id === id)?.data as CanvasNodeData | undefined)?.type,
    [rfNodes]
  );

  const isValidConnection = useCallback(
    (connection: Connection) =>
      isBodyLinkAllowed({
        sourceType: nodeTypeById(connection.source),
        targetType: nodeTypeById(connection.target),
        sourceHandle: connection.sourceHandle,
        sourceId: connection.source,
      }),
    [nodeTypeById]
  );

  // ---- Handle edge start ----
  const onConnectStart: OnConnectStart = useCallback((_event, params) => {
    connectStartRef.current = { nodeId: params.nodeId, handleId: params.handleId };
    connectionMadeRef.current = false;
    setIsConnecting(true);
  }, []);

  // ---- Handle edge end (drop) ----
  const onConnectEnd: OnConnectEnd = useCallback(
    (event) => {
      if (!isConnecting) return;
      setIsConnecting(false);

      if (!(event instanceof MouseEvent)) return;

      const elementUnderCursor = document.elementFromPoint(
        event.clientX,
        event.clientY
      );

      const droppedOnPane = elementUnderCursor?.classList.contains("react-flow__pane");

      if (droppedOnPane && reactFlowWrapper.current) {
        const bounds = reactFlowWrapper.current.getBoundingClientRect();
        const position = project({
          x: event.clientX - bounds.left,
          y: event.clientY - bounds.top,
        });
        setDropPosition(position);
        setOpenDialog(true);
      } else if (!connectionMadeRef.current && connectStartRef.current) {
        // Dropped on top of an existing node, but no edge was created — isValidConnection
        // rejected it during the drag. Re-check the same rule here (via isValidConnection,
        // the single source of truth already wired to the canvas) purely to decide whether
        // to explain why, since onConnect's own guard can't run for this path.
        const targetNodeEl = (elementUnderCursor as HTMLElement | null)?.closest(".react-flow__node");
        const targetNodeId = targetNodeEl?.getAttribute("data-id") ?? null;
        if (targetNodeId && targetNodeId !== connectStartRef.current.nodeId) {
          const wasRejected = !isValidConnection({
            source: connectStartRef.current.nodeId,
            target: targetNodeId,
            sourceHandle: connectStartRef.current.handleId,
            targetHandle: null,
          } as Connection);
          if (wasRejected) {
            toast.warn(
              "This message can only connect to a Smart Delay. Add one in between to continue the flow.",
              { autoClose: 3000 }
            );
          }
        }
      }
    },
    [isConnecting, project, isValidConnection]
  );

  // ---- Close dialog when clicking on empty canvas ----
  const handleCanvasClick = useCallback(() => {
    setOpenDialog(false);
  }, []);

  useEffect(() => {
    setRfNodes(nodes as unknown as Node[]);
    setRfEdges(edges as unknown as Edge[]);
  }, [nodes, edges, setRfEdges, setRfNodes]);

  const onNodesChange = useCallback(
    (changes: NodeChange[]) => {
      setRfNodes((nds) => {
        const updated = applyNodeChanges(changes, nds);
        setNodes(updated as unknown as CanvasNode[]);
        return updated;
      });
    },
    [setNodes, setRfNodes]
  );

  const onEdgesChange = useCallback(
    (changes: EdgeChange[]) => {
      setRfEdges((eds) => {
        const updated = applyEdgeChanges(changes, eds);
        setEdges(updated as unknown as CanvasEdge[]);
        return updated;
      });
    },
    [setEdges, setRfEdges]
  );

  const onEdgesDelete = useCallback(
    (deleted: Edge[]) => {
      setRfEdges((eds) => {
        const remainingEdges = eds.filter((e) => !deleted.some((d) => d.id === e.id));
        setEdges(remainingEdges as unknown as CanvasEdge[]);
        return remainingEdges;
      });
    },
    [setEdges, setRfEdges]
  );

  const onNodesDelete = useCallback(
    (deleted: Node[]) => {
      setRfNodes((nds) => {
        const remainingNodes = nds.filter((n) => !deleted.some((d) => d.id === n.id));
        setNodes(remainingNodes as unknown as CanvasNode[]);
        return remainingNodes;
      });

      // Also cleanup dangling edges
      setRfEdges((eds) => {
        const remainingEdges = eds.filter((e) => !deleted.some((n) => n.id === e.source || n.id === e.target));
        setEdges(remainingEdges as unknown as CanvasEdge[]);
        return remainingEdges;
      });
    },
    [setNodes, setEdges, setRfNodes, setRfEdges]
  );

  // ---- Create edge connections ----
  const onConnect = useCallback(
    (params: Connection) => {
      setIsConnecting(false);
      // Reaching onConnect at all (for a real drag) means isValidConnection already accepted
      // it — lets onConnectEnd, which fires right after, tell a rejected drop apart from this one.
      connectionMadeRef.current = true;

      // isValidConnection already blocks this during the drag; repeated here because a
      // connection can still arrive programmatically, and so the refusal can say why.
      if (!isValidConnection(params)) {
        toast.warn(
          "This message can only connect to a Smart Delay. Add one in between to continue the flow.",
          { autoClose: 3000 }
        );
        return;
      }

      setRfEdges((eds) => {
        // Prevent duplicate connections from same source handle
        const alreadyConnected = eds.some(
          (edge) =>
            edge.source === params.source && edge.sourceHandle === params.sourceHandle
        );
        if (alreadyConnected) {
          toast.warn(`Source node is already connected!`, { autoClose: 1000 });
          return eds;
        }

        const updated = addEdge(
          {
            ...params,
            type: "custom",
            animated: true,
            markerEnd: { type: MarkerType.Arrow },
            data: { onDelete: (id: string) => deleteEdge(id) },
          },
          eds
        );
        setEdges(updated as unknown as CanvasEdge[]);
        return updated;
      });
    },
    [setEdges, setRfEdges, deleteEdge, isValidConnection]
  );


  const normalizeNodes = (nodes: CanvasNode[]): any => {
    return nodes.map((node) => {
      const nodeData = node.data as CanvasNodeData;

      // Modify node content safely
      const updatedContent = nodeData.content.map((content) => {
        if (content.type === "TEXT_BUTTON") {
          const buttonList = (content.data as any).buttons || [];

          if (buttonList.length === 0) {
            return {
              ...content,
              type: "TEXT_BUTTON",
            };
          }
        }
        return content;
      });

      return {
        ...node,
        data: {
          ...nodeData,
          content: updatedContent,
        },
      };
    });
  };

  const updateStartNodeQrFields = (fields: Record<string, unknown>) => {
    const currentNodes = useFlowStore.getState().nodes;
    const updatedNodes = currentNodes.map((node) => {
      const nodeData = node.data as CanvasNodeData;
      if (nodeData.type !== "FLOW_START") return node;

      const content = nodeData.content.map((item) => {
        if (item.type !== "FLOW_START") return item;
        return {
          ...item,
          data: { ...(item.data as Record<string, unknown>), ...fields },
        };
      });

      return {
        ...node,
        data: { ...nodeData, content },
      };
    });

    setNodes(updatedNodes as unknown as CanvasNode[]);
  };

  /**
   * QR codes are created by the save itself, one per selected keyword. This copies what came back
   * into the canvas so the panel shows the links and the next save sends the codes it already has
   * — the builder must not create or delete them, or the two sides would fight over ownership.
   */
  const applyServerQrCodes = (savedFlow: any) => {
    const startData = ((savedFlow?.flowContent?.nodes || []) as any[])
      .flatMap((node: any) => (node?.data?.content || []) as any[])
      .find((content: any) => content?.type === "FLOW_START")?.data;
    if (!startData) return;

    updateStartNodeQrFields({
      qrCodes: startData.qrCodes || [],
      qrCode: startData.qrCode || "",
      qrShortUrl: startData.qrShortUrl || "",
      qrKeyword: startData.qrKeyword || "",
    });

    if (!startData.qrTriggerEnabled) return;
    const requested: string[] = startData.qrKeywords?.length ? startData.qrKeywords : (startData.keywords || []);
    const created: string[] = (startData.qrCodes || []).map((qr: any) => qr.keyword);
    const missing = requested.filter((keyword) => !created.includes(keyword));
    if (missing.length > 0) {
      toast.warning(
        `No QR code could be created for ${missing.map((k) => `"${k}"`).join(", ")}. ` +
        `Check your plan's QR code limit and that WhatsApp is connected.`
      );
    }
  };

  const saveChanges = () => {

    if (!flowValidated()) {
      window.alert("Fill all the fields");
      return
    }
    else if (!businessId) {
      window.alert("BusinessId not present");
      return
    }

    const nodePayload = normalizeNodes(nodes);
    setBtnLoading(true);
    const payload: WhatsappFlow = {
      name: flowName,
      businessId: businessId,
      createdById: (staffId ?? ""),
      createdByName: (staff?.agentName ?? ""),
      phoneNumberId: phoneNumberId ?? businessDetail?.phoneNumberId,
      displayPhoneNumber: businessDetail?.phoneNumber,
      status: flowStatus,
      flowContent: {
        nodes: nodePayload,
        edges: edges.map((edge) => {
          if (edge.sourceHandle === null) {
            return {
              ...edge,
              sourceHandle: edge.source
            }
          }
          return edge
        })
      }
    };
    if (!!flowId)
      payload["flowId"] = flowId;

    axios.post(`${environment.baseUrl}/business/flow`, payload).then((response) => {
      const savedFlowId = response.data.flowId;
      setFlowId(savedFlowId);
      if (response.data.qrSyncWarning) {
        toast.warning(response.data.qrSyncWarning);
      } else {
        toast.success(`Changes saved with name ` + flowName);
      }
      setBtnLoading(false);

      const startContent = nodes[0]
        ? ((nodes[0].data as CanvasNodeData).content || []).find((c: CanvasNodeContent) => c.type === "FLOW_START")
        : null;
      const sheetData = startContent?.data as any;
      if (sheetData?.sheetTriggerEnabled && sheetData?.sheetSpreadsheetId && sheetData?.sheetTab && sheetData?.sheetPhoneColumn) {
        axios.post(`${environment.baseUrl}/google/sheets/flow-trigger`, {
          businessId,
          flowId: savedFlowId,
          spreadsheetId: sheetData.sheetSpreadsheetId,
          spreadsheetName: sheetData.sheetSpreadsheetName,
          sheetName: sheetData.sheetTab,
          phoneColumn: (sheetData.sheetPhoneColumn as string).toUpperCase(),
          attributeMappings: sheetData.sheetMappings || []
        }).catch(() => {});
      } else if (savedFlowId && !sheetData?.sheetTriggerEnabled) {
        axios.delete(`${environment.baseUrl}/google/sheets/flow-trigger/${savedFlowId}?businessId=${businessId}`)
          .catch(() => {});
      }

      // Facebook Lead Ads trigger
      if (sheetData?.fbLeadTriggerEnabled && sheetData?.fbLeadFormId && sheetData?.fbLeadPageId) {
        axios.post(`${environment.baseUrl}/facebook/lead-trigger`, {
          businessId,
          flowId:        savedFlowId,
          pageId:        sheetData.fbLeadPageId,
          pageName:      sheetData.fbLeadPageName,
          formId:        sheetData.fbLeadFormId,
          formName:      sheetData.fbLeadFormName,
          fieldMappings: sheetData.fbLeadFieldMappings || []
        }).catch(() => {});
      } else if (savedFlowId && !sheetData?.fbLeadTriggerEnabled) {
        axios.delete(`${environment.baseUrl}/facebook/lead-trigger/${savedFlowId}?businessId=${businessId}`)
          .catch(() => {});
      }

      if (!response.data.qrSyncWarning) applyServerQrCodes(response.data);
    }).catch((error) => {
      toast.error(error.response.data.message);
      setBtnLoading(false);
    })
  }
  const [flowName, setFlowName] = useState<string>("Untitled");

  const flowValidated = (): boolean => {
    // Node-level checks first — these sit on the node itself rather than inside a content bubble,
    // so the per-content pass below cannot see them.
    const goalsNamed = nodes.every((node) => {
      const data: any = node.data;
      return !data?.isGoal || (data?.goalName?.trim().length > 0);
    });
    if (!goalsNamed) return false;

    return nodes.every((node) =>
      ((node.data as any).content as CanvasNodeContent[]).every((content) => {
        if (content.type === "FLOW_START") {
          const data: any = content.data;
          const triggerType = data?.triggerType || "KEYWORD"; // legacy fallback for flows saved before triggerType existed
          // Both ad-id shapes count: `adSourceIds` is current, the `adSourceId` scalar is what
          // flows saved before multi-ad support still carry. A flow must not stop being publishable
          // just because nobody has reopened its start node since.
          // A regex on its own is a complete trigger — the matcher falls through to it when no
          // keyword matches, so requiring a keyword beside it blocked the save for no reason.
          const primaryTriggerValid = triggerType === "CTWA"
            ? (data?.adSourceIds?.length > 0 || !!data?.adSourceId?.trim())
            : (data?.keywords?.length > 0 || !!data?.regex?.trim());
          // A fully-configured Facebook Lead Trigger is a sufficient entry point on its own.
          // Google Sheet Trigger intentionally does NOT get the same exemption.
          const fbLeadTriggerValid = !!(data?.fbLeadTriggerEnabled && data?.fbLeadPageId && data?.fbLeadFormId);
          return primaryTriggerValid || fbLeadTriggerValid;
        } else if (content.type === "TEXT_BUTTON") {
          return ((content.data as any).text?.length > 0);
        } else if (content.type === "LIST") {
          const data: any = content.data;
          const mode = data?.mode || (data?.arrayAttribute ? "dynamic" : "static");
          const commonValid =
            (data?.header?.trim().length > 0) &&
            (data?.footer?.trim().length > 0) &&
            (data?.body?.trim().length > 0) &&
            (data?.buttonText?.trim().length > 0);

          if (mode === "dynamic") {
            return commonValid &&
              (data?.arrayAttribute?.trim().length > 0) &&
              (data?.titlePath?.trim().length > 0);
          } else {
            return commonValid &&
              Array.isArray(data?.sections) &&
              data.sections.length > 0 &&
              data.sections.every((sec: any) => {
                if (!sec.sectionTitle || sec.sectionTitle.trim().length === 0) return false;
                if (!Array.isArray(sec.items) || sec.items.length === 0) return false;
                return sec.items.every(
                  (item: any) =>
                    item.title?.trim().length > 0 &&
                    item.description?.trim().length > 0
                );
              });
          }
        } else if (content.type === "MEDIA_BUTTON") {
          const data: any = content.data;
          return (data?.url?.length > 0) && (data?.fileType?.length > 0);
        }
        return true;
      })
    );
  }

  return (
    <>
      <CanvasHeader 
        saveChanges={saveChanges} 
        flowName={flowName} 
        setFlowName={setFlowName} 
        btnLoading={btnLoading} 
      />
      <div
        ref={reactFlowWrapper}
        className="reactflow-wrapper"
        style={{ width: "100vw", height: "100vh", position: "relative" }}
      >
        <BuilderSurface
          nodes={rfNodes}
          edges={rfEdges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onNodesDelete={onNodesDelete}
          onEdgesDelete={onEdgesDelete}
          onConnect={onConnect}
          isValidConnection={isValidConnection}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          onConnectStart={onConnectStart}
          onConnectEnd={onConnectEnd}
          onPaneClick={handleCanvasClick}
        >

        </BuilderSurface>

        {openDialog && (
          <div
            style={{
              position: "absolute",
              left: dropPosition.x,
              top: dropPosition.y,
              zIndex: 999,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <ContentMenu
              contents={addContentInfo}
              onAddSubNode={null}
              addNode={{ add: addComponent, nodes, setNodes, position: dropPosition }}
              closeDialog={() => setOpenDialog(false)}
            />
          </div>
        )}
      </div>
      <ToastContainer />
    </>
  );
}
